import { MarkerType } from "@vue-flow/core";
import type { ServiceRunStatus } from "@/types";

// Edge stroke colours for the workflow canvas — one source of truth for the editor's
// idle connectors and the history canvas's status-coloured edges. Mid-tone success/
// failure match the run-status palette so an edge into a node reads as the same green/
// red as that node's status icon and badge; legible on both light and dark canvas.
export const EDGE_COLORS = {
  // Theme token, not a fixed hex — the idle/structural connectors then read as the same grey as the
  // list view's hierarchy lines (.line-idle, also var(--bs-border-color)) and adapt to dark mode.
  // The stroke is applied as an inline SVG style under [data-bs-theme], so the var resolves correctly.
  idle:    'var(--bs-border-color)',
  active:  '#3b82f6',
  success: '#166534',
  failure: '#dc2626',
  // Darkened amber (amber-700) in the same spirit as success/failure — Bootstrap's --bs-warning is a
  // bright #ffc107 that washes out on a light canvas. Same family as the node's warning badge, tuned
  // for legibility on both themes. The list view's line-warning uses --bs-warning directly; a warning
  // edge here reads as the same amber without the wash-out.
  warning: '#b45309',
} as const;

/**
 * Maps a source service's run status to the VueFlow edge appearance. Edges are thin grey
 * structural connectors by default; they only take on colour/animation while the RUN is in
 * progress (runInProgress) — completed segments go solid coloured, running ones animate. A
 * finished/historical run renders all-grey edges (status is read from the node icons/badges).
 * Called with `(undefined, false)` for the editor's plain idle connectors.
 */
export function edgeStyleForStatus(status: ServiceRunStatus | undefined, runInProgress: boolean) {
  const idle = {
    style: { stroke: EDGE_COLORS.idle, strokeWidth: 1, strokeDasharray: "none" },
    markerEnd: undefined,
    // Said, not omitted. Edge updates are merged by id, so a flowing dot switched on for a live run is
    // still on for every render after it unless something turns it off — which is how a finished run
    // ends up with a grey line and a dot travelling along it.
    data: { showFlowingIcon: false },
  };
  // Completed / historical run — structure only, no status colour on the lines.
  if (!runInProgress) return idle;

  const arrow = (color: string) => ({
    type: MarkerType.ArrowClosed,
    color,
    width: 15,
    height: 15,
  });

  // Only the service actually running animates. Pending (not yet started) stays grey like the idle
  // structure — a blue line means "this is happening now", not "this is queued".
  if (status === 'Running') {
    return {
      animated: true,
      style: { stroke: EDGE_COLORS.active, strokeWidth: 2, strokeDasharray: "3,3" },
      markerEnd: arrow(EDGE_COLORS.active),
      data: { showFlowingIcon: true, iconColor: EDGE_COLORS.active, animationDuration: "1.5s" },
    };
  }
  // Settled segments are solid, not animated — a coloured line reads as a done outcome, so the flowing
  // dot is explicitly turned off (edge data merges by id, so it would otherwise linger from the blue phase).
  if (status === 'Failed') {
    return {
      animated: false,
      style: { stroke: EDGE_COLORS.failure, strokeWidth: 2, strokeDasharray: "none" },
      markerEnd: arrow(EDGE_COLORS.failure),
      data: { showFlowingIcon: false },
    };
  }
  if (status === 'Warning') {
    return {
      animated: false,
      style: { stroke: EDGE_COLORS.warning, strokeWidth: 2, strokeDasharray: "none" },
      markerEnd: arrow(EDGE_COLORS.warning),
      data: { showFlowingIcon: false },
    };
  }
  if (status === 'Succeeded') {
    return {
      animated: false,
      style: { stroke: EDGE_COLORS.success, strokeWidth: 2, strokeDasharray: "none" },
      markerEnd: arrow(EDGE_COLORS.success),
      data: { showFlowingIcon: false },
    };
  }
  // Pending / not-yet-started segment in a live run — neutral idle edge.
  return idle;
}
